import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RawDrillData } from '../drills/types'
import {
  NewPlayerSessionInput,
  RawTeamPersonSessionData
} from '../team_person_sessions/types'
import { getBlankSession } from './functions'
import { RawSessionData, ScoreboardType, SessionsState } from './types'

const localStorageSessionSetup = localStorage.getItem('sessionSetup')

export const initialSessionsState: SessionsState = {
  selectedId: null,
  selectedDrillId: null,
  activeId: null,
  setupSession: localStorageSessionSetup
    ? JSON.parse(localStorageSessionSetup)
    : // If local storage is empty, default to session of Soccer Training - Team Practice
      getBlankSession({
        sportType: 'Soccer',
        sessionType: 'Training',
        sessionSubType: 'TeamPractice'
      }),
  scoreboards: {}
}

export const sessionsSlice = createSlice({
  name: 'sessions',
  initialState: initialSessionsState,
  reducers: {
    setSelectedId: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload
    },
    setSelectedDrillId: (
      state,
      action: PayloadAction<RawDrillData['id'] | null>
    ) => {
      state.selectedDrillId = action.payload
    },
    setActiveId: (state, action: PayloadAction<string | null>) => {
      state.activeId = action.payload
    },
    setSetupSession: (state, action: PayloadAction<RawSessionData>) => {
      state.setupSession = action.payload
      localStorage.setItem('sessionSetup', JSON.stringify(action.payload))
    },
    updateSetupSession: (
      state,
      action: PayloadAction<Partial<RawSessionData>>
    ) => {
      const updatedSession = {
        ...state.setupSession,
        ...action.payload
      }
      state.setupSession = updatedSession
      localStorage.setItem('sessionSetup', JSON.stringify(updatedSession))
    },
    addPlayerSessionToSessionSetup: (
      state,
      action: PayloadAction<RawTeamPersonSessionData | NewPlayerSessionInput>
    ) => {
      const newPlayerSession = action.payload

      // If assigning a tag, first remove it from any other player
      if (newPlayerSession.hardwareId) {
        state.setupSession.players = state.setupSession.players.map(
          (playerSession) => {
            if (
              playerSession.hardwareId === newPlayerSession.hardwareId &&
              playerSession.teamPersonId !== newPlayerSession.teamPersonId
            ) {
              return { ...playerSession, hardwareId: null }
            }
            return playerSession
          }
        )
      }

      // Check if player already has a session
      const existingIndex = state.setupSession.players.findIndex(
        (playerSession) =>
          playerSession.teamPersonId === newPlayerSession.teamPersonId
      )

      if (existingIndex >= 0) {
        // Update existing player session
        state.setupSession.players[existingIndex] = newPlayerSession
      } else {
        // Add new player session
        state.setupSession.players.push(newPlayerSession)
      }

      localStorage.setItem('sessionSetup', JSON.stringify(state.setupSession))
    },
    removePlayerSessionFromSessionSetup: (
      state,
      action: PayloadAction<string>
    ) => {
      state.setupSession.players = state.setupSession.players.filter(
        (playerSession) => playerSession.id !== action.payload
      )

      localStorage.setItem('sessionSetup', JSON.stringify(state.setupSession))
    },
    removePlayerFromSessionSetup: (state, action: PayloadAction<string>) => {
      state.setupSession.players = state.setupSession.players.filter(
        (playerSession) => playerSession.teamPersonId !== action.payload
      )

      localStorage.setItem('sessionSetup', JSON.stringify(state.setupSession))
    },
    setPlayerTagInSessionSetup: (
      state,
      action: PayloadAction<{
        teamPersonId: string
        hardwareId: string | null
      }>
    ) => {
      const { teamPersonId, hardwareId } = action.payload

      // If assigning a tag, first remove it from any other player
      if (hardwareId) {
        state.setupSession.players = state.setupSession.players.map(
          (playerSession) => {
            if (
              playerSession.hardwareId === hardwareId &&
              playerSession.teamPersonId !== teamPersonId
            ) {
              return { ...playerSession, hardwareId: null }
            }
            return playerSession
          }
        )
      }

      // Now assign/remove the tag for the target player
      state.setupSession.players = state.setupSession.players.map(
        (playerSession) => {
          if (playerSession.teamPersonId === teamPersonId) {
            return { ...playerSession, hardwareId }
          }
          return playerSession
        }
      )

      localStorage.setItem('sessionSetup', JSON.stringify(state.setupSession))
    },
    addScoreboard: (
      state,
      action: PayloadAction<{ sessionId: string; scoreboard: ScoreboardType }>
    ) => {
      const { sessionId, scoreboard } = action.payload
      state.scoreboards[sessionId] = scoreboard
    }
  }
})

export const reducer = sessionsSlice.reducer

export const {
  setSelectedId,
  setActiveId,
  setSetupSession,
  updateSetupSession,
  addPlayerSessionToSessionSetup,
  removePlayerSessionFromSessionSetup,
  removePlayerFromSessionSetup,
  setPlayerTagInSessionSetup,
  addScoreboard,
  setSelectedDrillId
} = sessionsSlice.actions
